import { InlineMd } from "./markdown-view";
import { useState } from "react";
import { sessionTime } from "@foreman/app-core";
import type { InboxItem } from "@foreman/app-core";
import type { Nav } from "./nav";
import { RiskChip, RuleActions, riskOf } from "./approval-ui";
import { AgentLogo, Spinner, useApp } from "./ui";

export const agentKey = (a: string) => (a === "claude-code" ? "claude" : a === "codex" ? "codex" : a === "antigravity" ? "antigravity" : "other");
export const agentLabel = (a: string) => (a === "claude-code" ? "Claude" : a === "codex" ? "Codex" : a === "antigravity" ? "Antigravity" : a);

/**
 * An agent is waiting for an answer, shown at the top of Home. A background alert often never arrives while the app is open,
 * so the question and its buttons are right here: Approve or Deny in one tap, or open Activity for a question with options.
 */
export function AskCard({ item, online, nav }: { item: InboxItem; online: boolean; nav: Nav }) {
  const { hub } = useApp();
  const { computer, event } = item;
  const [busy, setBusy] = useState<null | "approve" | "deny">(null);
  const [error, setError] = useState<string | null>(null);

  const respond = async (action: "approve" | "deny") => {
    const conn = hub.get(computer.id);
    if (!conn || busy) return;
    setBusy(action);
    setError(null);
    try {
      await conn.respondInbox(event.id, action);
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(null);
  };

  return (
    <div className="ask-card" data-event={event.id} data-kind={event.kind} role="group" aria-label={event.kind === "approval" ? "Approval needed" : "Question for you"}>
      <div className="ask-head">
        <span className="ask-dot" aria-hidden />
        <span className="ask-kind">{event.kind === "approval" ? "Needs your approval" : "Has a question"}</span>
        <span className="ask-meta">{sessionTime(event.createdAt)}</span>
      </div>
      <div className="ask-title">{event.title}</div>
      <div className="ask-from">
        <AgentLogo agent={agentKey(event.agent)} size={13} />
        {agentLabel(event.agent)} · {computer.label}
      </div>
      {event.kind === "approval" && (
        <div className="ask-risk">
          <RiskChip risk={riskOf(event)} />
        </div>
      )}
      {event.kind === "approval" && event.detail && <pre className="ask-detail">{event.detail}</pre>}
      {event.kind === "question" && event.questions?.[0] && <div className="ask-detail plain"><InlineMd text={event.questions[0].question} /></div>}
      {event.kind === "approval" ? (
        <div className="ask-actions">
          <button className="btn secondary sm" disabled={!online || !!busy} onClick={() => void respond("deny")}>
            {busy === "deny" && <Spinner />}Deny
          </button>
          <button className="btn primary sm" disabled={!online || !!busy} onClick={() => void respond("approve")}>
            {busy === "approve" && <Spinner />}Approve
          </button>
        </div>
      ) : (
        <div className="ask-actions">
          <button className="btn primary sm" onClick={() => nav.goTab({ name: "inbox" })}>
            Answer
          </button>
        </div>
      )}
      {event.kind === "approval" && <RuleActions computerId={computer.id} asked={event} disabled={!online || !!busy} onDecide={(allow) => hub.get(computer.id)?.respondInbox(event.id, allow ? "approve" : "deny")} />}
      {!online && <div className="ask-note">Not connected to {computer.label}. You can respond once it reconnects.</div>}
      {error && (
        <div className="ask-note bad" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
